import React from 'react';
import { Award, Heart, Users, Music, Target, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { aboutContent, teachers } from '../data/mock';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">
              🎵 Our Heritage
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {aboutContent.title}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Preserving the timeless tradition of Hindustani classical music in the heart of Canada
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Musical Journey
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {aboutContent.story}
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-sky-50 p-8 rounded-xl border border-blue-100 max-w-3xl mx-auto">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center justify-center">
                <Target className="h-5 w-5 text-blue-600 mr-2" />
                Our Mission
              </h3>
              <p className="text-gray-700">
                {aboutContent.mission}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Achievements
            </h2>
            <p className="text-xl text-gray-600">
              Milestones that mark our dedication to musical excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {aboutContent.achievements.map((achievement, index) => (
              <Card key={index} className="bg-white border-blue-100 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 flex items-center space-x-4">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <CheckCircle className="h-6 w-6 text-blue-600" />
                  </div>
                  <p className="text-gray-700 font-medium">{achievement}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Teachers Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Meet Our Teacher
            </h2>
            <p className="text-xl text-gray-600">
              Expert instructor passionate about sharing musical knowledge
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {teachers.map((teacher) => (
              <Card key={teacher.id} className="group hover:shadow-xl transition-all duration-300 border-blue-100 overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
                    {/* Headshot - Circular Image */}
                    <div className="flex-shrink-0">
                      <img 
                        src={teacher.image} 
                        alt={teacher.name} 
                        className="w-48 h-48 object-cover object-top rounded-full shadow-lg group-hover:shadow-xl transition-shadow duration-300"
                      />
                    </div>
                    {/* Teacher Info */}
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{teacher.name}</h3>
                      <p className="text-lg text-blue-600 font-semibold mb-4">{teacher.title}</p>
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800 mb-4">
                        {teacher.specialization}
                      </Badge>
                      <p className="text-sm text-gray-600 font-medium mb-4">{teacher.qualifications}</p>
                      <p className="text-gray-700 leading-relaxed">{teacher.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-blue-100">
              The principles that guide our musical education
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-white/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Passion</h3>
              <p className="text-blue-100">
                We believe that passion is the key to unlocking musical potential in every student
              </p>
            </div>

            <div className="text-center">
              <div className="bg-white/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Excellence</h3>
              <p className="text-blue-100">
                We strive for excellence in teaching while maintaining the authenticity of classical traditions
              </p>
            </div>

            <div className="text-center">
              <div className="bg-white/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Community</h3>
              <p className="text-blue-100">
                We foster a welcoming community where music becomes a bridge between cultures
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;